import { arrayToString } from '@/helpers/utils'
import {
  Company,
  CompanyFilters,
  CompanySearchOrderBy,
} from 'generated/apolloComponents'
import { createContext } from 'react'

export type SelectType = {
  label: string
  value: string
}

export type CompaniesStateFilters = {
  city?: SelectType[]
  company?: string
  skills?: SelectType[]
  topTech?: boolean
}

// State
export type OrderStateType = {
  label: string
  value: CompanySearchOrderBy
}

export interface CompaniesState {
  page: number
  perPage: number
  companiesList?: Company[]
  filters?: CompaniesStateFilters
  order?: OrderStateType
  totalCount?: number
}

export const initialCompaniesState: CompaniesState = {
  page: 1,
  perPage: 10,
  companiesList: [],
  filters: undefined,
  order: undefined,
}

// Actions
export enum ActionType {
  ChangeFilter,
  ChangePage,
  ChangeOrder,
  SetCompaniesList,
  SetState,
}

export interface ChangeFilter {
  type: ActionType.ChangeFilter
  payload: CompaniesStateFilters
}

export interface ChangePage {
  type: ActionType.ChangePage
  payload: number
}

export interface ChangeOrder {
  type: ActionType.ChangeOrder
  payload: OrderStateType
}

export interface SetCompaniesList {
  type: ActionType.SetCompaniesList
  payload?: Company[]
}

export interface SetState {
  type: ActionType.SetState
  payload: CompaniesState
}

export type CompaniesActions =
  | ChangeFilter
  | ChangePage
  | SetCompaniesList
  | SetState
  | ChangeOrder

// Reducer
export function companiesReducer(
  state: CompaniesState,
  action: CompaniesActions
): CompaniesState {
  switch (action.type) {
    case ActionType.ChangeFilter:
      return { ...state, filters: action.payload, page: 1 }
    case ActionType.ChangePage:
      return { ...state, page: action.payload }
    case ActionType.ChangeOrder:
      return { ...state, order: action.payload }
    case ActionType.SetCompaniesList:
      return { ...state, companiesList: action.payload }
    case ActionType.SetState:
      return action.payload
    default:
      return state
  }
}

// Context
export const CompaniesContext = createContext<{
  state: CompaniesState
  dispatch: React.Dispatch<CompaniesActions>
}>({
  state: initialCompaniesState,
  dispatch: () => undefined,
})

export const convertProfilesStateFiltersToQueryFilters = (
  filters: CompaniesStateFilters
): CompanyFilters => {
  const parsed: CompanyFilters = {}
  let key: keyof CompaniesStateFilters

  for (key in filters) {
    const value = filters[key]

    if (value) {
      switch (key) {
        case 'company':
          parsed['companyName'] = value as string
          break
        case 'city':
        case 'skills':
          parsed[key] = (value as SelectType[]).map(
            (elem: SelectType) => elem['value']
          )
          break
      }
    }
  }

  return parsed
}

// Convert filters slug to URL
export const convertProfilesFiltersSlugToURL = (
  state: CompaniesState
): string => {
  let slug: string = ''
  let orderSlug: string = ''
  let filterSlug: string = ''
  let page: number | null = state.page ? state.page : null
  const filters: CompaniesStateFilters | null = state.filters
    ? state.filters
    : null

  if (filters) {
    filterSlug += '/s/'
    let key: keyof CompaniesStateFilters
    for (key in filters) {
      if (filters[key]) {
        let tmp: string | undefined

        switch (key) {
          case 'company':
            filterSlug += `company,${filters[key]}/`
            break
          case 'city':
          case 'skills':
            if ((filters[key] as SelectType[]).length !== 0) {
              tmp = arrayToString(filters[key] as SelectType[], 'value')
              filterSlug += `${key},${tmp}/`
            }
            break
        }
      }
    }
  }

  if (filterSlug == '/s/') filterSlug = ''

  let pageSlug = ''

  if (page && page > 1) {
    if (filterSlug === '') {
      pageSlug += '/s/'
    }
    pageSlug += `page,${state.page}`
  }

  return filterSlug + pageSlug
}
